import { useQuery } from '@tanstack/react-query';
import apiClient from '../utils/apiClient';
import { useTimeFrame } from '../contexts/TimeFrameContext';
import { EvaluationMetrics, EvaluationSummaryData, EvaluationTrendDataItem } from '../types/evaluation';
import { PERSIAN_MONTHS, MONTH_INDEX_MAP } from '../utils/dataHelpers';
import { useMemo } from 'react';

// Query keys for cache management
export const EVALUATION_QUERY_KEYS = {
  HEATMAP: 'evaluationHeatmap',
  TREND: 'evaluationTrend',
  SUMMARY: 'evaluationSummary',
  TWELVE_MONTH_AVERAGE: 'evaluationTwelveMonthAverage',
};

interface HeatmapResponse {
  branchData: EvaluationMetrics[];
  brandData: EvaluationMetrics[];
  allRestaurantsData: EvaluationMetrics[];
}

interface TrendResponse {
  branchTrend: EvaluationTrendDataItem[];
  brandTrend: EvaluationTrendDataItem[];
  allRestaurantsTrend: EvaluationTrendDataItem[];
}

/**
 * Hook to fetch evaluation heatmap data
 */
export function useEvaluationHeatmapData(branchId: number, months: number) {
  const queryResult = useQuery({
    queryKey: [EVALUATION_QUERY_KEYS.HEATMAP, branchId, months],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: HeatmapResponse }>(
        `/evaluation-dashboard/${branchId}/heatmap?months=${months}`
      );
      return response.data.data;
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Process data to create helper structures
  const processedData = useMemo(() => {
    const branchData = queryResult.data?.branchData || [];
    const brandData = queryResult.data?.brandData || [];
    const allRestaurantsData = queryResult.data?.allRestaurantsData || [];

    // Sort data by month number
    const sortedData = [...branchData].sort((a, b) => {
      const getMonthNumber = (monthStr: string | undefined) => {
        if (!monthStr) return 0;
        const monthName = typeof monthStr === 'string' && monthStr.includes('-') 
          ? monthStr.split('-')[0] 
          : monthStr;
        return MONTH_INDEX_MAP[monthName as keyof typeof MONTH_INDEX_MAP] || 0;
      };
      
      return getMonthNumber(a?.month) - getMonthNumber(b?.month);
    });

    // Create a complete month list based on the data
    const allMonths: string[] = [];
    if (sortedData && sortedData.length > 0) {
      sortedData.forEach(item => {
        if (item && item.month) {
          allMonths.push(item.month);
        }
      });
      // Return reversed array to have chronological order (oldest to newest)
      allMonths.reverse();
    }

    // Map existing data to month
    const dataByMonth: { 
      byMonth: { [key: string]: { data: EvaluationMetrics, index: number } },
      byMonthName: { [key: string]: { data: EvaluationMetrics, index: number } } 
    } = {
      byMonth: {},
      byMonthName: {}
    };

    let index = -1;
    sortedData.forEach(item => {
      index += 1;
      if (!item || !item.month) return;
      
      const monthKey = item.month;
      dataByMonth.byMonth[monthKey] = { data: item, index };
      
      // Also map by month name without year prefix for flexible matching
      const monthNameOnly = typeof monthKey === 'string' && monthKey.includes('-') 
        ? monthKey.split('-')[0] 
        : monthKey;
      if (monthNameOnly) {
        dataByMonth.byMonthName[monthNameOnly] = { data: item, index };
      }
    });

    return {
      branchData: sortedData,
      brandData,
      allRestaurantsData,
      allMonths,
      dataByMonth
    };
  }, [queryResult.data]);

  return {
    ...processedData,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch
  };
}

/**
 * Hook to fetch evaluation trend data
 */
export function useEvaluationTrendData(branchId: number, metric: string, months?: number) {
  const { timeFrame } = useTimeFrame();
  
  // Convert timeFrame to months if not provided
  const getMonths = () => {
    if (months) return months;
    switch (timeFrame) {
      case 'last1Month': return 1;
      case 'last3Months': return 3;
      case 'last6Months': return 6;
      case 'lastYear': return 12;
      default: return 6;
    }
  };

  return useQuery({
    queryKey: [EVALUATION_QUERY_KEYS.TREND, branchId, metric, getMonths()],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: TrendResponse }>(
        `/evaluation-dashboard/${branchId}/trend?metric=${metric}&months=${getMonths()}`
      );
      return response.data.data;
    },
    enabled: !!branchId && !!metric,
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => ({
      trendData: data.branchTrend || [],
      brandTrendData: data.brandTrend || [],
      allRestaurantsTrendData: data.allRestaurantsTrend || []
    })
  });
}

/**
 * Hook to fetch evaluation summary data
 */
export function useEvaluationSummaryData(branchId: number, months?: number) {
  const { timeFrame } = useTimeFrame();
  
  // Convert timeFrame to months if not provided
  const getMonths = () => {
    if (months) return months;
    switch (timeFrame) {
      case 'last1Month': return 1;
      case 'last3Months': return 3;
      case 'last6Months': return 6;
      case 'lastYear': return 12;
      default: return 6;
    }
  };

  return useQuery({
    queryKey: [EVALUATION_QUERY_KEYS.SUMMARY, branchId, getMonths()],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: EvaluationSummaryData }>(
        `/evaluation-dashboard/${branchId}/summary?months=${getMonths()}`
      );
      return response.data.data;
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => ({ summaryData: data })
  });
}

/**
 * Hook to fetch 12-month average data for evaluation metrics
 */
export function useEvaluation12MonthAverage(branchId: number) {
  return useQuery({
    queryKey: [EVALUATION_QUERY_KEYS.TWELVE_MONTH_AVERAGE, branchId],
    queryFn: async () => {
      const response = await apiClient.get<{ 
        success: boolean; 
        data: { 
          averages: Record<string, number>;
          monthCount: number;
        } 
      }>(
        `/evaluation-dashboard/${branchId}/metrics/12month-average`
      );
      return response.data.data;
    },
    enabled: !!branchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}